import React, { PureComponent } from 'react';
import { Flex } from '../../../../common/Flex';
import TimePeriodUtils from '../../../../../common/util/TimePeriodUtils';
import TimeUtils from '../../../../../common/util/TimeUtils';
import { FlightDto, ScenarioAttributesDto, ScenarioType } from '../../../../../model/CORE';
import FlightLevelUtils from '../../../../../common/util/FlightLevelUtils';
import FlightTable, {
    FlightColumn,
    FLIGHT_SELECTED_COLUMN,
    StandardFlightColumn,
    StandardFlightColumnV2,
} from '../../../../table/FlightTable';
import KeyUtils from '../../../../../common/util/KeyUtils';
import memoizeOne from 'memoize-one';
import { MULTIPLE_SELECTION, MultipleSelection } from '../../../../../common/table/ManagedTable';
import { Checkbox } from 'antd';

interface ScenarioDescriptionProps {
    selectedScenario: ScenarioAttributesDto;
    selectedFlights: FlightDto[];
    onFLightsSelected: (flights: FlightDto[]) => void;
    isFlightListLoading: boolean;
    flightList: FlightDto[];
}

interface ScenarioDescriptionState {
    isDisplayingFlightInCommonOnly: boolean;
}

export default class ScenarioDescription extends PureComponent<ScenarioDescriptionProps, ScenarioDescriptionState> {
    constructor(props) {
        super(props);
        this.state = {
            isDisplayingFlightInCommonOnly: false,
        };
    }
    componentDidUpdate(prevProps: Readonly<ScenarioDescriptionProps>) {
        if (prevProps.selectedScenario != this.props.selectedScenario) {
            this.setState({
                isDisplayingFlightInCommonOnly: false,
            });
        }
    }

    SIMPLE_FLIGHT_COLUMNS: FlightColumn[] = [
        FLIGHT_SELECTED_COLUMN,
        StandardFlightColumn.ENTRY_TIME,
        StandardFlightColumn.EXIT_TIME,
        StandardFlightColumnV2.ARCID,
        StandardFlightColumn.ATYPE,
        StandardFlightColumn.ADEP,
        StandardFlightColumn.ADES,
        StandardFlightColumn.CFMU_TYPE,
        StandardFlightColumn.ATOT_TTOT,
        StandardFlightColumn.CTOT,
        StandardFlightColumn.ATOT,
        StandardFlightColumnV2.xTA,
        StandardFlightColumn.MPR,
        StandardFlightColumn.DELAY,
        StandardFlightColumn.CONVECTION_IMPACT,
    ];

    private flightSelections = memoizeOne(
        (selection): MultipleSelection<FlightDto> => ({
            selectionMode: MULTIPLE_SELECTION,
            onSelectionChange: this.props.onFLightsSelected,
            selectedItems: selection,
        }),
    );

    render() {
        let flightListToDisplay = this.props.flightList;
        if (flightListToDisplay && this.state.isDisplayingFlightInCommonOnly) {
            flightListToDisplay = flightListToDisplay.filter(
                value1 =>
                    this.props.selectedScenario.problematicCapturedFlights.find(
                        value2 =>
                            value1.flightId.id === value2.id ||
                            value1.flightId.flightKeys.callsign === value2.flightKeys.callsign,
                    ) != null,
            );
        }
        return (
            <>
                <div className={'scenario-description'}>
                    <Flex grow={false} style={{ flexDirection: 'row', padding: '.5em' }}>
                        <Flex className={'scenario-description-title'}>Publication Status</Flex>
                        <Flex style={{ flex: '80%', flexDirection: 'row' }}>
                            {this.props.selectedScenario.publicationStatus}
                        </Flex>
                    </Flex>
                    <Flex grow={false} style={{ flexDirection: 'row', padding: '.5em' }}>
                        <Flex className={'scenario-description-title'}>Applicability Period</Flex>
                        <Flex style={{ flex: '80%', flexDirection: 'row' }}>
                            {TimePeriodUtils.formatDDHHMM(this.props.selectedScenario.applicabilityPeriod)}
                        </Flex>
                    </Flex>
                    <Flex grow={false} style={{ flexDirection: 'row', padding: '.5em' }}>
                        <Flex className={'scenario-description-title'}>Last update</Flex>
                        <Flex style={{ flex: '80%', flexDirection: 'row' }}>
                            {TimeUtils.formatDateDDMMYYYY(this.props.selectedScenario.lastModifiedTimeStamp)}
                        </Flex>
                    </Flex>
                    <Flex grow={false} style={{ flexDirection: 'row', padding: '.5em' }}>
                        <Flex className={'scenario-description-title'}>Scenario Type</Flex>
                        <Flex style={{ flex: '80%', flexDirection: 'row' }}>
                            {this.props.selectedScenario.scenarioType}
                        </Flex>
                    </Flex>
                    {this.props.selectedScenario.alternativeIcaoRoutePortions != null &&
                        this.props.selectedScenario.alternativeIcaoRoutePortions.length > 0 && (
                            <Flex grow={false} style={{ flexDirection: 'row', padding: '.5em' }}>
                                <Flex className={'scenario-description-title'}>Alternative route portion</Flex>
                                <Flex style={{ flex: '80%', flexDirection: 'column' }}>
                                    {this.props.selectedScenario?.alternativeIcaoRoutePortions
                                        ? this.props.selectedScenario.alternativeIcaoRoutePortions.map(
                                              (value, index) => <Flex key={index}>{value}</Flex>,
                                          )
                                        : '/'}
                                </Flex>
                            </Flex>
                        )}
                    {this.props.selectedScenario.scenarioType === ScenarioType.FL && (
                        <Flex grow={false} style={{ flexDirection: 'row', padding: '.5em' }}>
                            <Flex className={'scenario-description-title'}>Flight Level constraints</Flex>
                            <Flex style={{ flex: '80%', flexDirection: 'column' }}>
                                {this.props.selectedScenario.levelConstraints
                                    ? this.props.selectedScenario.levelConstraints.map((value, index) => (
                                          <Flex key={index}>
                                              {value.levelConstraintKind} FL
                                              {FlightLevelUtils.getLevelValue(value.flightLevel)}
                                          </Flex>
                                      ))
                                    : '/'}
                            </Flex>
                        </Flex>
                    )}
                    <Flex grow={false} style={{ flexDirection: 'row', padding: '.5em' }}>
                        <Flex className={'scenario-description-title'}>From</Flex>
                        <Flex style={{ flex: '80%', flexDirection: 'row' }}>{this.props.selectedScenario.from}</Flex>
                    </Flex>
                    <Flex grow={false} style={{ flexDirection: 'row', padding: '.5em' }}>
                        <Flex className={'scenario-description-title'}>To</Flex>
                        <Flex style={{ flex: '80%', flexDirection: 'row' }}>{this.props.selectedScenario.to}</Flex>
                    </Flex>
                    <Flex grow={false} style={{ flexDirection: 'row', padding: '.5em' }}>
                        <Flex className={'scenario-description-title'}>Traffic Volumes</Flex>
                        <Flex style={{ flex: '80%', flexDirection: 'row' }}>
                            {this.props.selectedScenario.scenarioPublishedTrafficVolumeIds
                                ? this.props.selectedScenario.scenarioPublishedTrafficVolumeIds.map((value, index) => (
                                      <Flex grow={false} style={{ padding: '.2em' }} key={index}>
                                          {value}
                                      </Flex>
                                  ))
                                : '/'}
                        </Flex>
                    </Flex>
                    <Flex grow={false} style={{ flexDirection: 'row', padding: '.5em' }}>
                        <Flex shrink={false} grow={false} className={'scenario-description-title'}>
                            Offloaded Airspaces
                        </Flex>
                        <Flex shrink={false} grow={false} style={{ flex: '80%', flexDirection: 'row' }}>
                            {this.props.selectedScenario.offLoadAirspaceIds
                                ? this.props.selectedScenario.offLoadAirspaceIds.join(' ')
                                : '/'}
                        </Flex>
                    </Flex>
                    <Flex grow={false} style={{ flexDirection: 'row', padding: '.5em' }}>
                        <Flex shrink={false} grow={false} className={'scenario-description-title'}>
                            Onloaded Airspaces
                        </Flex>
                        <Flex shrink={false} grow={false} style={{ flex: '80%', flexDirection: 'row' }}>
                            {this.props.selectedScenario.onLoadAirspaceIds
                                ? this.props.selectedScenario.onLoadAirspaceIds.join(' ')
                                : '/'}
                        </Flex>
                    </Flex>
                    <Flex grow={false} style={{ flexDirection: 'row', padding: '.5em' }}>
                        <Flex className={'scenario-description-title'}>Disabled Restriction</Flex>
                        <Flex style={{ flex: '80%', flexDirection: 'row' }}>
                            {this.props.selectedScenario.disabledRestrictionIds
                                ? this.props.selectedScenario.disabledRestrictionIds.join(' ')
                                : '/'}
                        </Flex>
                    </Flex>
                    <Flex grow={false} style={{ flexDirection: 'row', padding: '.5em' }}>
                        <Flex className={'scenario-description-title'}>Ignored Restriction</Flex>
                        <Flex style={{ flex: '80%', flexDirection: 'row' }}>
                            {this.props.selectedScenario.ignoredRestrictionIds
                                ? this.props.selectedScenario.ignoredRestrictionIds.join(' ')
                                : '/'}
                        </Flex>
                    </Flex>
                    {this.props.selectedScenario.comments && (
                        <Flex grow={false} style={{ flexDirection: 'row', padding: '.5em' }}>
                            <Flex className={'scenario-description-title'}>Comments</Flex>
                            <Flex style={{ flex: '80%', flexDirection: 'row' }}>
                                {this.props.selectedScenario.comments}
                            </Flex>
                        </Flex>
                    )}
                </div>
                <Flex style={{ borderTop: 'solid 1px #8C8B82', flexDirection: 'column', padding: '.5em' }}>
                    {this.props.flightList && (
                        <Flex grow={false} direction={'horizontal'} className={'scenario-description'}>
                            {this.props.selectedScenario.problematicCapturedFlights &&
                                this.props.selectedScenario.problematicCapturedFlights.length > 0 && (
                                    <Flex>
                                        {this.props.flightList.length} Flights Found,{' '}
                                        {this.props.selectedScenario.problematicCapturedFlights.length} in Common with
                                        selected TV
                                    </Flex>
                                )}
                            {(!this.props.selectedScenario.problematicCapturedFlights ||
                                this.props.selectedScenario.problematicCapturedFlights.length === 0) && (
                                <Flex>{this.props.flightList.length} Flights Found</Flex>
                            )}
                            {this.props.selectedScenario.problematicCapturedFlights &&
                                this.props.selectedScenario.problematicCapturedFlights.length > 0 && (
                                    <Flex>
                                        <Checkbox
                                            checked={this.state.isDisplayingFlightInCommonOnly}
                                            onChange={e => {
                                                this.setState({ isDisplayingFlightInCommonOnly: e.target.checked });
                                            }}></Checkbox>
                                        only display flights in common
                                    </Flex>
                                )}
                        </Flex>
                    )}
                    <Flex grow={true}>
                        <FlightTable
                            data={flightListToDisplay}
                            columns={this.SIMPLE_FLIGHT_COLUMNS}
                            keyProvider={KeyUtils.FLIGHT_KEY_PROVIDER}
                            loading={this.props.isFlightListLoading}
                            selection={this.flightSelections(this.props.selectedFlights)}
                            noDataMessage={'No flights captured in this time period'}
                        />
                    </Flex>
                </Flex>
            </>
        );
    }
}
